/**
 * Task List View Component
 *
 * Displays tasks in a compact table format with single row per record
 */

import { format } from 'date-fns';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { TaskDrawerRouter } from '@/components/tasks/task-drawer-router';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { UpdateTaskApiRequest } from '@/types/api/tasks';
import type { TaskStatus, UnifiedTask } from '@/types/tasks';

interface TaskListViewProps {
  readonly tasks: UnifiedTask[];
  readonly onTaskStatusChange: (taskId: string, newStatus: TaskStatus) => void;
  readonly onSave?: (taskId: string, data: UpdateTaskApiRequest) => void;
  readonly onComplete?: (taskId: string) => void;
}

interface GroupedTasks {
  open: UnifiedTask[];
  completed: UnifiedTask[];
}

export function TaskListView({ tasks, onTaskStatusChange, onSave, onComplete }: TaskListViewProps) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['open']));
  const [selectedTask, setSelectedTask] = useState<UnifiedTask | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleTaskClick = (task: UnifiedTask) => {
    setSelectedTask(task);
    setIsDrawerOpen(true);
  };

  // Group tasks by status
  const groupedTasks: GroupedTasks = tasks.reduce(
    (acc, task) => {
      if (task.status === 'completed') {
        acc.completed.push(task);
      } else {
        acc.open.push(task);
      }
      return acc;
    },
    {
      open: [],
      completed: [],
    } as GroupedTasks,
  );

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(group)) {
        newSet.delete(group);
      } else {
        newSet.add(group);
      }
      return newSet;
    });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Critical':
        return 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800';
      case 'High':
        return 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800';
      case 'Medium':
        return 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800';
      case 'Low':
        return 'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700';
    }
  };

  const renderTaskGroup = (title: string, groupKey: string, tasks: UnifiedTask[], color: string) => {
    const isExpanded = expandedGroups.has(groupKey);

    return (
      <Card key={groupKey}>
        <CardHeader
          className="cursor-pointer hover:bg-accent/50 transition-colors py-3"
          onClick={() => toggleGroup(groupKey)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle className="text-lg font-semibold">{title}</CardTitle>
              <Badge variant="secondary" className="h-6 px-2">
                {tasks.length}
              </Badge>
            </div>
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </div>
        </CardHeader>
        {isExpanded && (
          <CardContent className="p-0">
            {tasks.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No tasks</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-y">
                    <tr>
                      <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2">Type</th>
                      <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2">Task</th>
                      <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2">ID</th>
                      <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2">Priority</th>
                      <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2">Due Date</th>
                      <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2">Assigned To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.map((task, index) => (
                      <tr
                        key={task.id}
                        className={cn(
                          'border-b hover:bg-accent/30 transition-colors',
                          index === tasks.length - 1 && 'border-b-0',
                        )}
                        style={{ borderLeftColor: color, borderLeftWidth: '3px', borderLeftStyle: 'solid' }}
                      >
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="text-xs whitespace-nowrap">
                            {task.sourceType}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="max-w-md">
                            <button
                              onClick={() => handleTaskClick(task)}
                              className="font-medium text-sm truncate text-left hover:text-primary hover:underline cursor-pointer transition-colors"
                            >
                              {task.title}
                            </button>
                            <div className="text-xs text-muted-foreground truncate">{task.description}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleTaskClick(task)}
                            className="text-sm text-muted-foreground hover:text-primary hover:underline cursor-pointer transition-colors"
                          >
                            {task.sourceId}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            variant="outline"
                            className={cn('text-xs whitespace-nowrap', getPriorityColor(task.priority))}
                          >
                            {task.priority}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm whitespace-nowrap">{format(task.dueDate, 'dd/MM/yyyy')}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm">{task.assignedTo}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        )}
      </Card>
    );
  };

  return (
    <>
      <div className="space-y-4">
        {renderTaskGroup('Open Tasks', 'open', groupedTasks.open, '#3b82f6')}
        {renderTaskGroup('Completed', 'completed', groupedTasks.completed, '#10b981')}
      </div>

      <TaskDrawerRouter
        task={selectedTask}
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        onSave={onSave}
        onComplete={onComplete}
      />
    </>
  );
}
